import { useAuth } from "@/context/AuthContext";
import { fetchDocumentTypes } from "@/services/documentsApi";
import { useQuery } from "@tanstack/react-query";
import { fetchAuthSession } from "aws-amplify/auth";

/**
 * Hook to get access token for API calls
 */
async function getAccessToken(): Promise<string> {
  const session = await fetchAuthSession();
  const accessToken = session.tokens?.accessToken?.toString();
  if (!accessToken) {
    throw new Error("No access token available");
  }
  return accessToken;
}

/**
 * Hook to fetch available document types for filter dropdowns
 *
 * Returns distinct document types from the database.
 * Data is cached for 5 minutes since document types change infrequently.
 *
 * @returns Query result with types array, loading state, and error
 */
export function useDocumentTypes() {
  const { isAuthenticated } = useAuth();

  return useQuery<string[], Error>({
    queryKey: ["documentTypes"],
    queryFn: async () => {
      const token = await getAccessToken();
      return fetchDocumentTypes(token);
    },
    enabled: isAuthenticated,
    staleTime: 300000, // Consider data stale after 5 minutes
    gcTime: 600000, // Keep in cache for 10 minutes
  });
}
